/**
 * The apps a student can jump to from the LMS header (the app switcher), and where each one's
 * SIGN-IN DOOR is.
 *
 * Each `href` is the app's student SSO door, not its home page: a shared school computer may still
 * hold another student's session in that app, which a bare link would open. The door discards it
 * and signs in silently as whoever the IdP knows in this browser (`/auth/login?prompt=none` in the
 * Öğrenci Paneli, `/api/auth/birey/login?silent=1` in Sınav.Link), so the destination's own session
 * and access checks stay authoritative.
 *
 * ⚠️ The deployment supplies the hosts (`REACT_APP_OGRENCI_PANEL_URL`, `REACT_APP_SINAVLINK_URL`,
 * `REACT_APP_DERSLIK_URL`, set per environment in ci.yml's frontend-build job). There is NO production or staging fallback in
 * browser code: an unset, malformed, non-https or credential-bearing value draws no icon at all, so
 * the switcher can never send a student to a guessed host, and a staging build can never carry a
 * production one by default. (An icon with nowhere to go would be a dead link, so it is not drawn.)
 *
 * Derslik's door is its silent sign-in (`/login?silent=1`), landing on `/app`. Derslik refuses a kurum
 * that does not hold its product at `/erisim-yok`, so the icon needs no check of its own. A further
 * app is a new row below and a variable in ci.yml.
 */

export type StudentAppKey = 'ogrenci-paneli' | 'sinavlink' | 'derslik';

export interface StudentApp {
  key: StudentAppKey;
  /** The icon's tooltip and its accessible name, and the big word on the redirect screen. */
  name: string;
  /** The app's student SSO door. */
  href: string;
  /** The app's mark, drawn on the icon and on the redirect screen. */
  mark: string;
  /**
   * The redirect screen's second line, per app: the comp's own for Sınav.Link; the comp has none for the
   * Öğrenci Paneli (it is the comp), so this one is worded as its LMS line is, and is the SAME line the
   * Sınav.Link switcher shows for it (keep the two in step).
   */
  sub: string;
  /** What DigiKoç says from the corner of the redirect screen (a wide window only). */
  say: string;
}

/** The base must be https, with no credentials in it; anything else is not an address we send a student to. */
function entry(base: string | undefined, path: string): string | null {
  if (!base?.trim()) return null;
  try {
    const url = new URL(base.trim());
    if (url.protocol !== 'https:' || url.username || url.password) return null;
    return new URL(path, url).href;
  } catch {
    return null;
  }
}

/** The Öğrenci Paneli's door: a silent check against the IdP (`prompt=none`). */
export const PANEL_DOOR = '/auth/login?prompt=none';
/** Sınav.Link's door: the silent sign-in, landing on the student's panel. */
export const SINAVLINK_DOOR = '/api/auth/birey/login?silent=1&next=%2Fpanel';

/** Derslik's door: the silent sign-in, landing in the app. */
export const DERSLIK_DOOR = '/login?silent=1&next=%2Fapp';

export function studentApps(
  panel: string | undefined,
  sinavlink: string | undefined,
  derslik?: string | undefined,
): StudentApp[] {
  const panelHref = entry(panel, PANEL_DOOR);
  const examHref = entry(sinavlink, SINAVLINK_DOOR);
  const derslikHref = entry(derslik, DERSLIK_DOOR);
  const apps: StudentApp[] = [];
  if (panelHref) {
    apps.push({
      key: 'ogrenci-paneli',
      name: 'Öğrenci Paneli',
      href: panelHref,
      mark: '/app-icons/ogrenci-paneli.webp',
      sub: 'Genel analizin ve çalışma planın hazırlanıyor. Tek oturumla güvenle bağlanıyorsun.',
      say: 'Görüşürüz!',
    });
  }
  if (examHref) {
    apps.push({
      key: 'sinavlink',
      name: 'Sınav.Link',
      href: examHref,
      mark: '/sinavlink-icon.svg',
      sub: 'Sınav sonuçların ve kayıtların yükleniyor. Tek oturumla güvenle bağlanıyorsun.',
      say: 'Sınavlarında bol şans!',
    });
  }
  if (derslikHref) {
    apps.push({
      key: 'derslik',
      name: 'Derslik',
      href: derslikHref,
      mark: '/app-icons/derslik.png',
      sub: 'Canlı derslerin ve sınıfların hazırlanıyor. Tek oturumla güvenle bağlanıyorsun.',
      say: 'Derste görüşürüz!',
    });
  }
  return apps;
}

/** The apps this build was configured with: read once, as the build bakes the variables in. */
export function configuredStudentApps(): StudentApp[] {
  return studentApps(
    process.env.REACT_APP_OGRENCI_PANEL_URL,
    process.env.REACT_APP_SINAVLINK_URL,
    process.env.REACT_APP_DERSLIK_URL,
  );
}
